// Q9 S3 — Per-section PATCH + final SUBMIT client wrappers.
// Hits the endpoints shipped in S2 (apps/api/src/routes/v1/visit-report-sections.ts).
// Used by the VisitReportWizard (one PATCH per step nav) and by the carer
// portal (Carer-parity S3a swaps the bespoke form for the wizard with
// submissionChannel="mobile"; the wizard threads its useFetcher() through
// these calls so the carer subtree's carerFetch — with silent refresh —
// is used for every PATCH/submit hit).
//
// Carer-parity S3a: each function now accepts a final optional `fetcher`
// arg defaulting to apiFetch. Background offline-queue replay (which runs
// at app root above any subtree) keeps using the default — apiFetch reads
// whichever bearer the active BearerProvider is exposing, so both
// coordinator and carer subtrees replay correctly under last-writer-wins.

import type { VisitType, SubmitInput } from "@hcm/core";
import {
  vitalsPatchSchema, mobilityPatchSchema, clinicalPatchSchema,
  wellbeingPatchSchema, medicationPatchSchema, notesPatchSchema,
} from "@hcm/core";
import { apiFetch } from "../lib/api.js";
import type { SectionStep } from "../lib/visit-report-conditional-rules.js";

export type Fetcher = <T>(path: string, init?: RequestInit) => Promise<T>;

export type SectionName =
  | "vitals" | "mobility" | "clinical" | "wellbeing" | "medication" | "notes";

export const SECTION_NAMES: readonly SectionName[] = [
  "vitals", "mobility", "clinical", "wellbeing", "medication", "notes",
] as const;

// TASK-48 S2 — Field-name lists per section are now derived from the
// canonical Zod schema shapes in @hcm/core. Renaming a schema field
// updates this map automatically; the previous hand-written literal
// strings could (and did at Q9 S6) drift from the schema.
//
// `Object.keys(schema.shape)` enumerates the picked keys at runtime;
// the typed cast preserves the narrow `keyof SubmitInput` element type
// for downstream consumers (extractSectionPayload + tests).
export const FIELDS: Record<SectionName, readonly (keyof SubmitInput)[]> = {
  vitals:     Object.keys(vitalsPatchSchema.shape)     as (keyof SubmitInput)[],
  mobility:   Object.keys(mobilityPatchSchema.shape)   as (keyof SubmitInput)[],
  clinical:   Object.keys(clinicalPatchSchema.shape)   as (keyof SubmitInput)[],
  wellbeing:  Object.keys(wellbeingPatchSchema.shape)  as (keyof SubmitInput)[],
  medication: Object.keys(medicationPatchSchema.shape) as (keyof SubmitInput)[],
  notes:      Object.keys(notesPatchSchema.shape)      as (keyof SubmitInput)[],
};

/**
 * Extract the section's owned subset from a monolithic API-shaped payload.
 * Used by the wizard (per step) and by the carer-page submit shim.
 */
export function extractSectionPayload(
  monolithic: Record<string, unknown>, section: SectionName,
): Record<string, unknown> {
  const subset: Record<string, unknown> = {};
  for (const field of FIELDS[section]) {
    if (field in monolithic) subset[field] = monolithic[field];
  }
  return subset;
}

export async function patchSection(
  srId: string, section: SectionName, payload: Record<string, unknown>,
  fetcher: Fetcher = apiFetch,
): Promise<unknown> {
  return fetcher(`/sr/${srId}/visit-report/sections/${section}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export interface SubmitBody {
  completed_at: string;
  submission_channel: "crm" | "mobile" | "whatsapp" | "api";
  /**
   * Q9 S4 / D19 — the wizard-computed visible step list for this submission.
   * Backend uses it to scope the completeness check to revealed sections only
   * (e.g., don't require Mobility complete if it wasn't shown). Required from
   * S4 onward; legacy clients that omit it are rejected with 422 (the spec's
   * recommended posture — submit must declare its visible step list).
   */
  visible_steps: readonly SectionStep[];
  visit_location?: string | null;
  gps_lat?: number | null;
  gps_lng?: number | null;
  gps_accuracy_m?: number | null;
}

export async function submitVisitReport(
  srId: string, body: SubmitBody,
  fetcher: Fetcher = apiFetch,
): Promise<unknown> {
  return fetcher(`/sr/${srId}/visit-report/submit`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// Q9 S4 — Step-0 fallback wrapper. Hits the existing PATCH /sr/:id endpoint
// with just the visit_type field; the SR Zod schema (visit_type is nullable
// optional via S2) accepts it. Successful PATCH triggers the wizard's single
// visibleSteps recompute (D6 exception).
export async function patchSrVisitType(
  srId: string, visitType: VisitType,
  fetcher: Fetcher = apiFetch,
): Promise<unknown> {
  return fetcher(`/sr/${srId}`, {
    method: "PATCH",
    body: JSON.stringify({ visit_type: visitType }),
  });
}

/**
 * Carer-portal submit shim: scatters a monolithic payload into 6 sequential
 * PATCHes + final SUBMIT. Retained for the offline-replay path; the live
 * carer flow now mounts <VisitReportWizard submissionChannel="mobile"> which
 * scatters via patchSection / submitVisitReport directly.
 */
export async function submitFromMonolithic(
  srId: string, monolithic: Record<string, unknown>, body: SubmitBody,
  fetcher: Fetcher = apiFetch,
): Promise<unknown> {
  for (const section of SECTION_NAMES) {
    const subset = extractSectionPayload(monolithic, section);
    if (Object.keys(subset).length === 0) continue;
    await patchSection(srId, section, subset, fetcher);
  }
  return submitVisitReport(srId, body, fetcher);
}
